import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import HeaderApp from "../../components/HeaderApp";
import { getCursosActivosConPreguntas } from "../../services/cursosService";
import { logAxiosError } from "../../utils/logAxiosError";
import { useResolvableImageSrc } from "../../hooks/useResolvableImageSrc";

const CourseCover = ({ raw, alt }) => {
  const src = useResolvableImageSrc(raw);
  if (!src) {
    return <Typography variant="body2" color="text.secondary">Sin imagen</Typography>;
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
};

const normalizarCurso = (curso) => ({
  id: curso?.idCurso ?? curso?.id ?? curso?.IdCurso,
  nombre: curso?.Nombre ?? curso?.nombre ?? "Sin nombre",
  descripcion: curso?.Descripcion ?? curso?.descripcion ?? "",
  imagen: curso?.ImagenPortada ?? curso?.imagenPortada ?? "",
  estado: String(curso?.Estado ?? curso?.estado ?? "0"),
  ultimaCalificacion:
    curso?.UltimaCalificacion ?? curso?.ultimaCalificacion ?? curso?.calificacion ?? null,
  aprobo: curso?.Aprobo ?? curso?.aprobo ?? null,
  totalPreguntasAsociadas:
    curso?.TotalPreguntasAsociadas ?? curso?.totalPreguntasAsociadas ?? 0,
  fechaFinalizacion: curso?.FechaFinalizacion ?? curso?.fechaFinalizacion ?? null,
});

const getColorCalificacion = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "text.secondary";
  if (n < 60) return "error.main";
  if (n < 80) return "warning.main";
  return "success.main";
};

const AvailableCourses = () => {
  const navigate = useNavigate();
  const PAGE_SIZE = 6;
  const [cursos, setCursos] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchCursos = async () => {
    try {
      setLoadingCursos(true);
      const response = await getCursosActivosConPreguntas();
      const list = Array.isArray(response) ? response : response?.data ?? [];
      setCursos(Array.isArray(list) ? list : []);
    } catch (error) {
      logAxiosError("Estudiante: getCursos (cursos disponibles)", error);
      setCursos([]);
    } finally {
      setLoadingCursos(false);
    }
  };

  useEffect(() => {
    fetchCursos();
  }, []);

  const cursosActivos = useMemo(() => cursos.map(normalizarCurso), [cursos]);

  const cursosFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cursosActivos;

    return cursosActivos.filter(
      (curso) =>
        curso.nombre.toLowerCase().includes(q) || curso.descripcion.toLowerCase().includes(q)
    );
  }, [cursosActivos, search]);

  const totalPages = Math.max(1, Math.ceil(cursosFiltrados.length / PAGE_SIZE));

  const cursosPaginados = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return cursosFiltrados.slice(start, start + PAGE_SIZE);
  }, [cursosFiltrados, page, PAGE_SIZE]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <>
      <HeaderApp />
      <Box
        component="main"
        sx={{
          flex: 1,
          py: 4,
          px: 2,
          bgcolor: "background.default",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Cursos disponibles
          </Typography>

          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Buscar cursos por nombre o descripción"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 320, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            <Typography variant="body2" color="text.secondary">
              {cursosFiltrados.length} curso{cursosFiltrados.length === 1 ? "" : "s"} disponible{cursosFiltrados.length === 1 ? "" : "s"}
            </Typography>
            {cursosFiltrados.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Pagina {page} de {totalPages}
              </Typography>
            )}
          </Box>

          {loadingCursos ? (
            <Box py={6} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {cursosPaginados.map((curso) => {
                const isAprobado = String(curso.aprobo) === "1";
                const puedeEntrar = !isAprobado;
                const calificacionTxt =
                  curso.ultimaCalificacion == null || Number.isNaN(Number(curso.ultimaCalificacion))
                    ? "Sin intentos"
                    : `${Number(curso.ultimaCalificacion).toFixed(1)}%`;
                const colorCalificacion = getColorCalificacion(curso.ultimaCalificacion);
                return (
                <Grid item xs={12} sm={6} md={4} key={`curso-disponible-${curso.id}`}>
                  <Card
                    role={puedeEntrar ? "button" : undefined}
                    tabIndex={puedeEntrar ? 0 : -1}
                    onClick={() => {
                      if (puedeEntrar) navigate(`/curso/${curso.id}`);
                    }}
                    onKeyDown={(e) => {
                      if (puedeEntrar && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        navigate(`/curso/${curso.id}`);
                      }
                    }}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: puedeEntrar ? "pointer" : "not-allowed",
                      transition: "transform .2s ease, box-shadow .2s ease",
                      border: isAprobado ? "1px solid #2E7D32" : "1px solid transparent",
                      backgroundColor: isAprobado ? "#F1F8E9" : "inherit",
                      "&:hover": {
                        transform: puedeEntrar ? "translateY(-3px)" : "none",
                        boxShadow: puedeEntrar ? 3 : 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 160,
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <CourseCover raw={curso.imagen} alt={curso.nombre} />
                    </Box>
                    <CardContent sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {curso.nombre}
                        </Typography>
                        {isAprobado && (
                          <Chip
                            size="small"
                            label="Aprobado"
                            sx={{
                              color: "#1B5E20",
                              borderColor: "#2E7D32",
                              backgroundColor: "#E8F5E9",
                              fontWeight: 700,
                            }}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color={curso.ultimaCalificacion == null ? "text.secondary" : colorCalificacion}
                        sx={{ mt: 0.5, display: "block", fontWeight: curso.ultimaCalificacion == null ? 400 : 700 }}
                      >
                        Ultima calificación: {calificacionTxt}
                      </Typography>
                      {!puedeEntrar && (
                        <Typography variant="caption" color="success.dark" sx={{ display: "block", fontWeight: 600, mt: 0.5 }}>
                          Curso completado. No disponible para reingreso.
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {curso.descripcion || "Sin descripcion"}
                      </Typography>
                    </CardContent>
                    {isAprobado && (
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/curso/${curso.id}/certificado`, {
                              state: {
                                nombreCurso: curso.nombre,
                                fechaFinalizacion: curso.fechaFinalizacion,
                              },
                            });
                          }}
                        >
                          Generar y descargar certificado
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              );
              })}
              {!loadingCursos && cursosFiltrados.length === 0 && (
                <Grid item xs={12}>
                  <Box py={7} textAlign="center" sx={{ border: "1px dashed #ddd", borderRadius: 2 }}>
                    <Typography color="text.secondary">
                      No se encontraron cursos activos con ese criterio.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Prueba buscando por otro nombre o descripción.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {!loadingCursos && cursosFiltrados.length > PAGE_SIZE && (
            <Box mt={3} display="flex" justifyContent="center">
              <Pagination
                page={page}
                count={totalPages}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#616161",
                    borderColor: "#BDBDBD",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#E0E0E0 !important",
                    color: "#424242",
                    borderColor: "#9E9E9E",
                    fontWeight: 600,
                  },
                }}
                onChange={(_, newPage) => {
                  setPage(newPage);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default AvailableCourses;
